class SectionTabs extends HTMLElement {
  static CATEGORY_PARAM = 'category';

  constructor() {
    super();
    this.isVideosTabs = this.classList.contains('section-videos-tabs');
    this.onPopState = this.onPopState.bind(this);
  }

  connectedCallback() {
    this.hydrateTabsFromTemplates();

    this.tabs = this.querySelectorAll('.section__tab-button');
    this.tabsContent = this.querySelectorAll('.section__tabs-content');
    this.tabsScroller = this.querySelector('[data-tabs-scroll]');
    this.prevArrow = this.querySelector('.section__tabs-arrow--prev');
    this.nextArrow = this.querySelector('.section__tabs-arrow--next');
    this.stickyNav = this.querySelector('.section__tabs-nav');

    // Deeplink wins over "active by default"; ensureActiveTab is the fallback
    // when no ?category= match exists and Liquid left every tab inactive.
    const openedFromUrl = this.isVideosTabs && this.applyCategoryFromUrl({ scroll: true });
    if (!openedFromUrl) this.ensureActiveTab();

    this.initTabs();
    this.initScroll();
    this.initCategoryDeeplinks();

    // In the theme editor, merchants can select a video block from the
    // sidebar without clicking its tab first - the block has to already be
    // in the live DOM for Shopify's editor to find and highlight it. Skip
    // the lazy-loading optimization entirely while editing; it only matters
    // for real storefront page loads.
    if (window.Shopify?.designMode) {
      this.tabsContent.forEach((tabContent) => this.hydrateTabVideos(tabContent));
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  hydrateTabsFromTemplates() {
    // Category theme blocks can only read their own settings, so the nav
    // button title is rendered as an inert <template> fragment inside each
    // block's own content panel. Hoist it into the shared nav row.
    // Videos: Tabs already does this synchronously via an inline script in
    // sections/videos-tabs.liquid (to avoid a late CLS-causing pop-in), so
    // this is a no-op there and only runs for other section-tabs usages.
    if (this.dataset.tabsHydrated) return;
    this.dataset.tabsHydrated = 'true';

    const navContainer = this.querySelector('[data-tabs-scroll]');
    const navTemplates = this.querySelectorAll('template.section__tab-nav-template');
    if (navContainer && navTemplates.length) {
      navTemplates.forEach((template) => {
        navContainer.appendChild(template.content.cloneNode(true));
        template.remove();
      });
    }
  }

  ensureActiveTab() {
    if (!this.tabs || !this.tabs.length) return;
    const hasActiveTab = Array.from(this.tabs).some((tab) => tab.classList.contains('active'));
    if (hasActiveTab) return;

    this.activateTab(this.tabs[0], { scroll: false, updateUrl: false });
  }

  // Every category except the default/active one keeps its video cards
  // (images, videos, iframes) inert inside a <template> - nothing in there is
  // fetched or parsed by the browser until the tab is actually opened, which
  // keeps the initial page weight to roughly one tab's worth of videos no
  // matter how many categories/videos exist. This clones the real markup in
  // exactly once, the first time a given tab is shown.
  hydrateTabVideos(content) {
    if (!content) return;

    const container = content.querySelector('[data-video-cards]');
    const template = container?.querySelector('template.section__tab-videos-template');

    if (container && template) {
      container.appendChild(template.content.cloneNode(true));
      template.remove();
      window.reviveTemplateVideos?.(content);
    }

    window.initLazyVideos?.();
  }

  findTabByCategory(slug) {
    if (!slug || !this.tabs?.length) return null;
    return Array.from(this.tabs).find((tab) => tab.dataset.category === slug) || null;
  }

  getCategoryFromUrl() {
    return new URLSearchParams(window.location.search).get(SectionTabs.CATEGORY_PARAM);
  }

  setCategoryInUrl(slug) {
    if (window.Shopify?.designMode) return;

    const url = new URL(window.location.href);
    if (slug) {
      url.searchParams.set(SectionTabs.CATEGORY_PARAM, slug);
    } else {
      url.searchParams.delete(SectionTabs.CATEGORY_PARAM);
    }

    window.history.replaceState(window.history.state, '', url);
  }

  applyCategoryFromUrl({ scroll = false } = {}) {
    const slug = this.getCategoryFromUrl();
    if (!slug) return false;

    const tab = this.findTabByCategory(slug);
    if (!tab) return false;

    this.activateTab(tab, { scroll, updateUrl: false });
    return true;
  }

  activateTab(tab, { scroll = false, updateUrl = false } = {}) {
    if (!tab) return;

    this.tabs.forEach((otherTab) => otherTab.classList.remove('active'));
    tab.classList.add('active');

    this.tabsContent.forEach((tabContent) => tabContent.classList.remove('show'));
    const contentToActivate = document.getElementById(tab.dataset.id);
    if (contentToActivate) {
      contentToActivate.classList.add('show');
      this.hydrateTabVideos(contentToActivate);
    }

    tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

    if (updateUrl && tab.dataset.category) {
      this.setCategoryInUrl(tab.dataset.category);
    }

    if (scroll && this.isVideosTabs && contentToActivate) {
      window.setTimeout(() => {
        let headerHeight = 0;
        document.querySelectorAll('.shopify-section-group-header-group').forEach((header) => {
          headerHeight += header.offsetHeight;
        });

        const stickyNavHeight = this.stickyNav ? this.stickyNav.offsetHeight : 0;
        const targetPosition =
          contentToActivate.getBoundingClientRect().top + window.pageYOffset - headerHeight - stickyNavHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }, 100);
    }
  }

  initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activateTab(tab, { scroll: this.isVideosTabs, updateUrl: this.isVideosTabs });
      });
    });
  }

  initCategoryDeeplinks() {
    if (!this.isVideosTabs) return;
    window.addEventListener('popstate', this.onPopState);
  }

  onPopState() {
    if (!this.applyCategoryFromUrl({ scroll: true })) {
      this.ensureActiveTab();
    }
  }

  initScroll() {
    if (!this.tabsScroller) return;

    const updateArrows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.tabsScroller;
      const maxScroll = scrollWidth - clientWidth;
      const hasOverflow = maxScroll > 1;

      this.classList.toggle('has-tabs-overflow', hasOverflow);

      if (this.prevArrow) this.prevArrow.disabled = scrollLeft <= 1;
      if (this.nextArrow) this.nextArrow.disabled = scrollLeft >= maxScroll - 1;
    };

    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => {
        this.tabsScroller.scrollBy({ left: -this.tabsScroller.clientWidth * 0.6, behavior: 'smooth' });
      });
    }

    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => {
        this.tabsScroller.scrollBy({ left: this.tabsScroller.clientWidth * 0.6, behavior: 'smooth' });
      });
    }

    this.tabsScroller.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);

    updateArrows();
    window.setTimeout(updateArrows, 250);
  }
}

customElements.define('section-tabs', SectionTabs);

class SocialInteractions extends HTMLElement {
  constructor() {
    super();
    this.viewCount = Math.floor(Math.random() * (5000000 - 500 + 1)) + 500;
    this.likeCount = Math.floor(Math.random() * 500);
    this.dislikeCount = Math.floor(Math.random() * 200);
    this.handle = this.getAttribute('data-handle');
  }

  connectedCallback() {
    this.viewCountElement = this.querySelector('[data-views]');
    this.likeCountElement = this.querySelector('[data-like-count]');
    this.dislikeCountElement = this.querySelector('[data-dislike-count]');
    this.likeBtn = this.querySelector('[data-like]');
    this.dislikeBtn = this.querySelector('[data-dislike]');
    this.bookmarkIcon = this.querySelector('[data-bookmark]');

    this.init();
    this.updateButtonState();
    this.updateBookmarkState();

    this.likeBtn.addEventListener('click', () => this.handleLike());
    this.dislikeBtn.addEventListener('click', () => this.handleDislike());
    this.bookmarkIcon.addEventListener('click', () => this.handleBookmark());
  }

  formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  }

  init() {
    this.viewCountElement.textContent = `${this.formatNumber(this.viewCount)}+ Views`;
    this.likeCountElement.textContent = this.formatNumber(this.likeCount);

    if (this.dislikeCountElement)
      this.dislikeCountElement.textContent = this.formatNumber(this.dislikeCount);
  }

  updateButtonState() {
    if (this.likeCount > 0) {
      this.dislikeBtn.disabled = true;
    } else if (this.dislikeCount > 0) {
      this.likeBtn.disabled = true;
    } else {
      this.likeBtn.disabled = false;
      this.dislikeBtn.disabled = false;
    }
  }

  updateBookmarkState() {
    if (!this.handle) return;
    
    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '{}');
    if (bookmarks[this.handle]) {
        this.bookmarkIcon.classList.add('active');
    } else {
        this.bookmarkIcon.classList.remove('active');
    }
  }

  handleLike() {
    this.likeCount++;
    this.likeCountElement.textContent = this.formatNumber(this.likeCount);
    this.dislikeCount = Math.max(this.dislikeCount - 1, 0);
    if (this.dislikeCountElement) this.dislikeCountElement.textContent = this.formatNumber(this.dislikeCount);
    this.updateButtonState();
  }

  handleDislike() {
    this.dislikeCount++;
    if (this.dislikeCountElement) this.dislikeCountElement.textContent = this.formatNumber(this.dislikeCount);
    this.likeCount = Math.max(this.likeCount - 1, 0);
    this.likeCountElement.textContent = this.formatNumber(this.likeCount);
    this.updateButtonState();
  }

  handleBookmark() {
    if (!this.handle) return;
    
    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '{}');
    
    if (this.bookmarkIcon.classList.contains('active')) {
        this.bookmarkIcon.classList.remove('active');
        delete bookmarks[this.handle];
    } else {
        this.bookmarkIcon.classList.add('active');
        bookmarks[this.handle] = true;
    }
    
    localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
  }
}

if (!customElements.get('social-interactions')) {
  customElements.define('social-interactions', SocialInteractions);
}

document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
  anchor.addEventListener("click", function (e) {
    const targetId = anchor.getAttribute('href').slice(1);
    if (targetId && targetId !== 'recover') {
      e.preventDefault();
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
});

document.querySelectorAll('video').forEach((videoEl) => {
  videoEl.addEventListener('play', function() {
    if (videoEl.getAttribute('data-played') !== 'true') {
      videoEl.setAttribute('data-played', 'true');
    }
  });
});

document.querySelectorAll('.media__play-icon').forEach((mediaPlay) => {
  mediaPlay.addEventListener('click', function() {
    mediaPlay.classList.add('hidden');

    const video = this.closest('.media')?.querySelector('video');
    if (video) {
      video.setAttribute('data-played', 'true');
      video.play();
    }
  });
});

function copyDiscountCode(button) {
  // Get the discount code from data-text attribute
  const discountCode = button.getAttribute('data-text');
  
  // Copy to clipboard
  navigator.clipboard.writeText(discountCode).then(() => {
    // Find the tooltip span element
    const tooltip = button.querySelector('.tooltip');
    if (!tooltip) return;
    
    // Change tooltip text to "Copied!"
    tooltip.textContent = 'Copied!';
    
    // Reset tooltip text after 2 seconds
    setTimeout(() => {
      tooltip.textContent = 'Copy';
    }, 2000);
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
}

function openColorDrawer() {
  const drawerOpener = 
    document.querySelector('.drawer-opener') || 
    document.querySelector('.colour-drawer-button') ||
    document.querySelector('[data-open-colour-drawer]');
    
  if (drawerOpener) {
    drawerOpener.click();
  }
}

function initColorDrawerLinks() {
  document.querySelectorAll('a[data-choose-colour="true"]').forEach(link => {
    link.addEventListener('click', function(event) {
      event.preventDefault();
      
      const linkUrl = new URL(this.href, window.location.origin);
      const currentUrl = new URL(window.location.href);
      
      if (linkUrl.pathname === currentUrl.pathname) {
        openColorDrawer();
      } else {
        sessionStorage.setItem('openColourDrawer', 'true');
        window.location.href = this.href;
      }
    });
  });
}

function checkColorDrawerOpen() {
  if (sessionStorage.getItem('openColourDrawer') === 'true') {
    sessionStorage.removeItem('openColourDrawer');
    setTimeout(() => {
      openColorDrawer();
    }, 500);
  }
}
document.addEventListener('DOMContentLoaded', function() {
  initColorDrawerLinks();
  checkColorDrawerOpen();
  document.addEventListener('shopify:section:load', initColorDrawerLinks);
});

const scrollToEls = document.querySelectorAll('[data-scroll-link]');
scrollToEls.forEach(scrollToEl => {
  scrollToEl.addEventListener('click', function (e) {
    e.preventDefault();

    const targetId = scrollToEl.getAttribute('data-scroll-link');
    const targetEl = document.getElementById(targetId);

    if (!targetEl) return;

    if (scrollToEl.closest('details')) {
      scrollToEl.closest('details').open = false;
    }

    let headerHeight = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach(header => {
      headerHeight += header.offsetHeight;
    });

    let scrollGap = 24;
    if (window.matchMedia('(max-width: 989px)').matches) {
      scrollGap = 100;
    }

    const targetPosition =
      targetEl.getBoundingClientRect().top + window.pageYOffset - headerHeight - scrollGap;

    console.log(targetPosition);

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  });
});